/**
 * V4 draft local-mirror — single source of truth (iter314, Feb 27 2026).
 *
 * Both Stepper (CustomStudioBuilder) and Visual (VisualStudioBuilder) read
 * and write to the SAME localStorage key so that:
 *
 *   • An anonymous customer who builds in Stepper and toggles to Visual
 *     does NOT lose their fabric / option / note picks.
 *   • An anonymous customer who builds in Visual and toggles to Stepper
 *     sees their selections waiting on the other side.
 *   • Mode-toggle + "back" navigation are non-destructive — the local
 *     mirror is the safety net that survives every page hop.
 *   • Maya chat messages (keyed elsewhere by clientRequestId) stay aligned
 *     because the same clientRequestId is persisted here and re-used by
 *     the destination builder.
 *
 * Server-side draft remains the source of truth for signed-in customers;
 * this local mirror is purely a write-through cache that survives logout
 * / mode-switches / refreshes.
 */
const V4_DRAFT_KEY = "jns_v4_custom_draft";

/**
 * iter321 (Feb 27 2026) — Cross-tab edit signaller.
 *
 * Each save bumps a monotonically increasing `seq` field. Other tabs
 * subscribed via `subscribeToOtherTabEdits` see the change and can
 * warn the customer / refetch state. Without this, two tabs would
 * silently clobber each other's last writes via debounced auto-save.
 */
function nextSeq(prev) {
    const last = Number(prev?.seq || 0);
    return Number.isFinite(last) ? last + 1 : 1;
}

export function subscribeToOtherTabEdits(onOtherTabEdit) {
    if (typeof window === "undefined") return () => {};
    // Capture our own seq at subscribe time so we ignore writes WE made.
    let lastKnownSeq = (loadV4Draft()?.seq) || 0;
    function handler(e) {
        if (e.key !== V4_DRAFT_KEY) return;
        if (!e.newValue) return;
        try {
            const parsed = JSON.parse(e.newValue);
            const seq = Number(parsed?.seq || 0);
            if (seq > lastKnownSeq) {
                lastKnownSeq = seq;
                onOtherTabEdit(parsed);
            }
        } catch { /* malformed — ignore */ }
    }
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
}

export function loadV4Draft() {
    try {
        const raw = localStorage.getItem(V4_DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.state) return null;
        return parsed;
    } catch { return null; }
}

export function saveV4Draft({ step, state, clientRequestId, serverId, selfie } = {}) {
    try {
        if (!state) return;
        // Don't write empty drafts (no outfit picked) — would just churn
        // the mirror with INITIAL_STATE rewrites.
        if (!state.outfit_template_id) return;
        const prev = loadV4Draft();
        localStorage.setItem(V4_DRAFT_KEY, JSON.stringify({
            step: step || 1,
            state,
            client_request_id: clientRequestId || null,
            server_id: serverId || null,
            // iter314 — selfie travels too so Visual's photo step persists
            // across mode toggles.
            selfie: selfie || null,
            savedAt: Date.now(),
            // iter321 — monotonic seq so other tabs can detect concurrent edits.
            seq: nextSeq(prev),
        }));
    } catch { /* quota — silently skip */ }
}

export function clearV4Draft() {
    try { localStorage.removeItem(V4_DRAFT_KEY); } catch { /* noop */ }
}

/**
 * iter315 (Feb 27 2026) — Maya chat key cleanup.
 *
 * Maya persists her transcript under keys like:
 *   jns_maya_v4_studio_msgs__<clientRequestId>__<sessionId>
 *   jns_maya_v4_studio_session__<clientRequestId>
 *   jns_maya_v4_open__<scopeKey>
 *
 * When a customer starts a brand-new design (fresh=1), switches to a
 * DIFFERENT outfit, or just placed an order, these stale keys should
 * be wiped so they don't accumulate AND so the next draft doesn't show
 * Apply buttons referencing the old fabrics.
 *
 * Pass `keepClientRequestId` to keep ONLY the keys tied to that
 * clientRequestId (used right after a save when we want to preserve
 * the current active conversation but nuke historical ones).
 */
export function clearMayaChatKeys({ keepClientRequestId = null } = {}) {
    try {
        const prefixes = [
            "jns_maya_v4_studio_msgs__",
            "jns_maya_v4_studio_session__",
            "jns_maya_v4_open__",
            "jns_maya_msgs_",
        ];
        const removeKeys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (!prefixes.some((p) => k.startsWith(p))) continue;
            if (keepClientRequestId && k.includes(keepClientRequestId)) continue;
            removeKeys.push(k);
        }
        removeKeys.forEach((k) => {
            try { localStorage.removeItem(k); } catch { /* noop */ }
        });
    } catch { /* noop */ }
}

/**
 * Nuke ALL custom-studio local state. Used by:
 *   • "Begin Custom Outfit" ?fresh=1 entry
 *   • Successful order submission
 *   • Cross-outfit switch (customer picked a different outfit than the
 *     local draft holds)
 */
export function nukeAllV4LocalState() {
    clearV4Draft();
    clearMayaChatKeys();
}

/**
 * iter316 (Feb 27 2026) — Auth-transition cleanup.
 *
 * Wider net than `nukeAllV4LocalState`. Called by AuthContext on:
 *   • Sign-out (User → null) — so the next person on the shared device
 *     doesn't see the previous customer's design or Maya chat.
 *   • User switch (User A → User B) — so B doesn't inherit A's local
 *     draft, Maya transcript, or any cached preferences keyed to A.
 *
 * NOT called on:
 *   • Initial sign-in (null → User) — because the guest may have an
 *     in-flight design that should sync to their new account.
 *
 * Cleans:
 *   • The V4 draft mirror.
 *   • All Maya chat / session / open-state keys.
 *   • Global Maya session (`jns_ai_stylist_session`).
 *   • Step-0 dismissed flag (sessionStorage).
 *   • Style-profile dismissed flag (sessionStorage).
 *   • The Visual/Stepper "preferred design mode" preference is kept —
 *     it's a UX setting, not personal data, and re-applies cleanly for
 *     the next customer.
 */
export function clearCustomStudioOnAuthChange() {
    try {
        // 1) Draft mirror
        clearV4Draft();
        // 2) Maya chat / sessions / open-state
        clearMayaChatKeys();
        // 3) Global Maya stylist session (the shopping-side Maya share
        //    this key — clearing it forces a fresh session for the next
        //    user on the device).
        try { localStorage.removeItem("jns_ai_stylist_session"); } catch { /* noop */ }
        // 4) Session-scoped UX flags
        try { sessionStorage.removeItem("jns_v4_step0_skipped"); } catch { /* noop */ }
        try { sessionStorage.removeItem("jns_v4_maya_style_card_dismissed"); } catch { /* noop */ }
        // 5) Guest "My Designs" tile cache — owned by previous user.
        try { localStorage.removeItem("jns_guest_custom_designs"); } catch { /* noop */ }
    } catch {
        /* localStorage unavailable — noop */
    }
}

/**
 * Returns true if the local mirror's outfit_template_id matches `outfitId`
 * AND the draft was saved within the last `freshMs`. Used by both
 * builders to decide whether to hydrate from local storage instead of
 * seeding a brand-new template skeleton.
 */
export function isLocalDraftForOutfit(outfitId, freshMs = 24 * 60 * 60 * 1000) {
    if (!outfitId) return null;
    const d = loadV4Draft();
    if (!d) return null;
    if (d.state?.outfit_template_id !== outfitId) return null;
    if (d.savedAt && Date.now() - d.savedAt > freshMs) return null;
    return d;
}
